import { Plus } from 'lucide-react';
import { Link } from 'react-router';
import { useNotes } from '@/api/hooks';
import { LIBELLE_STATUT, type StatutDocument } from '@/api/types';
import { peutEmettreDocuments } from '@/auth/modules';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, EtapeBadge, PageHeader, StatutBadge, Vide } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useFiltresListe } from '@/lib/filtres-liste';
import { dateFr, montant } from '@/lib/utils';

const STATUTS = Object.keys(LIBELLE_STATUT) as StatutDocument[];

/** Notes explicatives du périmètre (TDR concerné, incidence budgétaire, niveau dans le circuit) ; onglet « À traiter » pour les validateurs (GEDEM v3.0, § 7). */
export function NoteListe() {
  const session = useSession();
  const u = session!.utilisateur;
  const peutEmettre = peutEmettreDocuments(u.habilitations);
  const valideur = u.etapes.length > 0;
  const { onglet, statut, setOnglet, setStatut } = useFiltresListe(valideur ? (['a-traiter', 'tous'] as const) : (['tous'] as const), valideur ? 'a-traiter' : 'tous', 'tous');
  const { data, isLoading, error } = useNotes(onglet === 'a-traiter' ? { aTraiter: true } : statut === 'TOUS' ? {} : { statut });
  return (
    <div>
      <PageHeader
        titre="Notes explicatives"
        sousTitre="Changements motivés sur un TDR déjà soumis ou autorisé : dates, équipes, lieux, activités ou budget. Circuit complet quel que soit le montant."
        actions={
          peutEmettre && (
            <Button asChild>
              <Link to="/ne/nouvelle">
                <Plus className="size-4" /> Nouvelle note
              </Link>
            </Button>
          )
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs value={onglet} onValueChange={(v) => setOnglet(v as 'a-traiter' | 'tous')}>
          <TabsList>
            {valideur && <TabsTrigger value="a-traiter">À traiter</TabsTrigger>}
            <TabsTrigger value="tous">{u.habilitations.some((h) => h.structureId === null) ? 'Toutes les notes' : 'Mes notes et celles de ma structure'}</TabsTrigger>
          </TabsList>
        </Tabs>
        {onglet === 'tous' && (
          <Select value={statut} onValueChange={(v) => setStatut(v as StatutDocument | 'TOUS')}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="TOUS">Tous les statuts</SelectItem>
              {STATUTS.map((s) => (
                <SelectItem key={s} value={s}>
                  {LIBELLE_STATUT[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      {isLoading ? (
        <Chargement />
      ) : error ? (
        <ErreurBloc erreur={error} />
      ) : !data?.length ? (
        <Vide message={onglet === 'a-traiter' ? 'Aucune note en attente de votre décision.' : 'Aucune note explicative.'} />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Numéro</TableHead>
                <TableHead>Objet</TableHead>
                <TableHead>TDR concerné</TableHead>
                <TableHead className="hidden md:table-cell">Incidence</TableHead>
                <TableHead className="text-right">Écart</TableHead>
                <TableHead className="hidden lg:table-cell">Émetteur</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="hidden sm:table-cell">Mis à jour</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((n) => (
                <TableRow key={n.id}>
                  <TableCell>
                    <Link to={`/ne/${n.id}`} className="font-medium text-primary hover:underline">
                      {n.numero ?? 'Brouillon'}
                    </Link>
                  </TableCell>
                  <TableCell className="max-w-[32ch] truncate">{n.objet}</TableCell>
                  <TableCell>
                    {n.tdr ? (
                      <Link to={`/tdr/${n.tdr.id}`} className="text-primary hover:underline">
                        {n.tdr.numero ?? 'TDR'}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <Badge variant={n.impactBudgetaire ? 'destructive' : 'outline'}>{n.impactBudgetaire ? 'Budgétaire' : 'Sans incidence'}</Badge>
                  </TableCell>
                  <TableCell className="num text-right">{n.impactBudgetaire ? `${n.ecart > 0 ? '+' : ''}${montant(n.ecart)}` : '—'}</TableCell>
                  <TableCell className="hidden lg:table-cell">{n.emetteur?.nom}</TableCell>
                  <TableCell className="space-x-1">
                    <StatutBadge statut={n.statut} />
                    {n.statut === 'SOUMIS' && <EtapeBadge etape={n.etapeCourante} />}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">{dateFr(n.updatedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
